import {Trans} from '@lingui/macro';
import React, {ReactNode, useMemo} from 'react';

import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {getHasNewContactsManagerAccounts} from '../../../store/featureFlags/featureFlagsSelectors';
import {useConditionCardProps, useConditionGroups} from '../../editors/conditions/conditionsHelpers';
import {ConditionsList} from '../../editors/conditions/conditionsList';
import {useRuleEditorContext} from '../../editors/ruleEditor/ruleEditorContext';
import {
  ClientRuleCondition,
  ruleEditorConditionsAccountGroups,
  ruleEditorConditionsGroups,
} from '../ruleConditionModel';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleEditorSection} from './ruleEditorSection';

/*
 * Types.
 */

interface RuleEditorConditionsProps {
  conditions: ReadonlyArray<ReadonlyArray<ClientRuleCondition>>;
  description?: ReactNode;
  notDisplayingErrorCards?: ReadonlyArray<string>;

  onAddCondition: EditorStateActions['onAddCondition'];
  onUpdateCondition: EditorStateActions['onUpdateCondition'];
  onDeleteCondition: EditorStateActions['onDeleteCondition'];
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Component.
 */

export const RuleEditorConditions = (props: RuleEditorConditionsProps) => {
  const {
    conditions: andConditions,
    description,
    notDisplayingErrorCards,
    onAddCondition,
    onUpdateCondition,
    onDeleteCondition,
    onValidation,
  } = props;

  const {isCustomView, isLibrary, ruleIntegration} = useRuleEditorContext();

  const hasNewContactsManagerAccounts = useAppSelector(getHasNewContactsManagerAccounts);
  const conditionGroups = useMemo(
    () => [
      ...ruleEditorConditionsGroups,
      ...(hasNewContactsManagerAccounts ? ruleEditorConditionsAccountGroups : []),
    ],
    [hasNewContactsManagerAccounts],
  );
  const ruleEditorConditionGroups = useConditionGroups(ruleIntegration, conditionGroups);

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();
  const isAdvancedEditor = (isLibrary ? shouldShowMoreOptions : true) && !isCustomView;

  const conditionsProps = useConditionCardProps(
    andConditions,
    (condition: ClientRuleCondition) => ruleIntegration.findConditionDefinition(condition),
    onUpdateCondition,
    onDeleteCondition,
    onValidation,
    (condition) => Boolean(notDisplayingErrorCards?.includes(condition.clientId)),
  );

  return (
    <RuleEditorSection
      description={description ?? <Trans id="rules-editor-section-header-if">If…</Trans>}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      <ConditionsList
        isAdvancedEditor={isAdvancedEditor}
        shouldHideAdvancedAddBlock={isCustomView}
        conditions={conditionsProps}
        conditionGroups={ruleEditorConditionGroups}
        onAddCondition={onAddCondition}
      />
    </RuleEditorSection>
  );
};
